/**
 * HasNewAuthPermission Directive
 * Structural directive to show/hide elements based on new-auth permissions only
 *
 * Usage:
 * <button *hasNewAuthPermission="'reports-create'">Create Report</button>
 * <div *hasNewAuthPermission="['reports-list', 'dashboards-list']; mode: 'any'">View Data</div>
 * <div *hasNewAuthPermission="['reports-export', 'reports-schedule']; mode: 'all'">Export Scheduled</div>
 *
 * Behavior:
 * - Applies permission checks only for users logged in through the new auth flow
 * - Acts as a pass-through for legacy login sessions
 *
 * Security Note:
 * This directive is ONLY for UX (hiding/showing UI elements)
 * Backend ALWAYS validates permissions on every API call
 * Never rely on this directive for security
 */

import {
  Directive,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import {
  PermissionCheck,
  PermissionService,
} from "../services/permission.service";

@Directive({
  selector: "[hasNewAuthPermission]",
  standalone: true,
})
export class HasNewAuthPermissionDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private permission: PermissionCheck | PermissionCheck[] | null = null;
  private mode: "any" | "all" = "any";
  private hasView = false;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private permissionService: PermissionService,
  ) {}

  @Input()
  set hasNewAuthPermission(permission: PermissionCheck | PermissionCheck[]) {
    this.permission = permission;
    this.updateView();
  }

  @Input()
  set hasNewAuthPermissionMode(mode: "any" | "all") {
    this.mode = mode;
    this.updateView();
  }

  ngOnInit(): void {
    this.permissionService.newAuthPermissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView(): void {
    const shouldShow =
      !this.permissionService.isNewAuthSession() || this.checkPermission();

    if (shouldShow && !this.hasView) {
      this.viewContainer.clear();
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!shouldShow && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private checkPermission(): boolean {
    if (!this.permission || !this.permissionService.isNewAuthSession()) {
      return false;
    }

    if (typeof this.permission === "string") {
      return this.permissionService.hasNewAuthPermission(this.permission);
    }

    if (Array.isArray(this.permission)) {
      if (!this.permission.length) {
        return false;
      }

      if (this.mode === "all") {
        return this.permissionService.hasAllNewAuthPermissions(this.permission);
      }

      return this.permissionService.hasAnyNewAuthPermission(this.permission);
    }

    return false;
  }
}
